import React, { useCallback, useRef } from 'react';
import type { CustomFilterProps } from 'ag-grid-react';
import { useGridFilter } from 'ag-grid-react';
import { FILTER_STYLES } from '../AgGridCompliance.config';

interface FilterOption {
  value: string;
  label: string;
}

interface FilterConfig {
  label: string;
  options: FilterOption[];
}

type FilterOptionsType = {
  assetStatus: FilterConfig;
  userAssetRelationship: FilterConfig;
};

type UserAssetFilterModel = {
  filterType: 'userAsset';
  assetStatus?: string;
  userAssetRelationship?: string;
} | null;

const filterOptions: FilterOptionsType = {
  assetStatus: {
    label: 'Asset Status',
    options: [
      { value: 'all', label: 'All' },
      { value: 'Assigned', label: 'Assigned' },
      { value: 'Unassigned', label: 'Unassigned' },
    ],
  },
  userAssetRelationship: {
    label: 'User–Asset Relationship',
    options: [
      { value: 'all', label: 'All' },
      { value: 'Connected', label: 'Connected' },
      { value: 'Missing', label: 'Missing' },
      { value: 'Multiple', label: 'Multiple' },
    ],
  },
};

const getValueFromModel = (model: UserAssetFilterModel, key: string): string => {
  if (!model) {
    return 'all';
  }
  return (model as any)[key] || 'all';
};

const sections = [
  { title: 'Asset', keys: ['assetStatus', 'userAssetRelationship'] as const },
];

const UserAssetFilter = ({ model, onModelChange }: CustomFilterProps<any, any, UserAssetFilterModel>) => {
  const refFirstDropdown = useRef<HTMLSelectElement>(null);

  const filterValues: { [key: string]: string } = {};
  Object.keys(filterOptions).forEach(key => {
    filterValues[key] = getValueFromModel(model, key);
  });

  const doesFilterPass = useCallback(() => {
    // Server-side filtering - always return true
    return true;
  }, []);

  const afterGuiAttached = useCallback(() => {
    refFirstDropdown.current?.focus();
  }, []);

  useGridFilter({ doesFilterPass, afterGuiAttached });

  const updateModel = (key: string, value: string) => {
    const newFilterValues = { ...filterValues, [key]: value };

    const hasActiveFilters = Object.values(newFilterValues).some(v => v !== 'all');

    if (!hasActiveFilters) {
      onModelChange(null);
      return;
    }

    const newModel: UserAssetFilterModel = {
      filterType: 'userAsset',
    };

    if (newFilterValues.assetStatus !== 'all') {
      newModel.assetStatus = newFilterValues.assetStatus;
    }
    if (newFilterValues.userAssetRelationship !== 'all') {
      newModel.userAssetRelationship = newFilterValues.userAssetRelationship;
    }

    onModelChange(newModel);
  };

  const handleReset = () => {
    onModelChange(null);
  };

  const handleDropdownChange = (key: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateModel(key, e.target.value);
  };

  const handleFocus = (e: React.FocusEvent<HTMLSelectElement | HTMLButtonElement>) => {
    e.currentTarget.style.border = FILTER_STYLES.focusBorder;
  };

  const handleBlur = (e: React.FocusEvent<HTMLSelectElement | HTMLButtonElement>) => {
    e.currentTarget.style.border = FILTER_STYLES.defaultBorder;
  };

  const renderOptions = (options: FilterOption[]) => 
    options.map(option => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ));

  let isFirstDropdown = true;

  return (
    <div style={FILTER_STYLES.container}>
      {sections.map(section => (
        <div key={section.title} style={FILTER_STYLES.section}>
          <h4 style={FILTER_STYLES.sectionHeading}>
            {section.title}
          </h4>
          {section.keys.map(filterKey => {
            const config = filterOptions[filterKey];
            const isFirst = isFirstDropdown;
            if (isFirst) {
              isFirstDropdown = false;
            }

            return (
              <div key={filterKey} style={FILTER_STYLES.fieldGroup}>
                <label style={FILTER_STYLES.label}>
                  {config.label}:
                </label>
                <select
                  ref={isFirst ? refFirstDropdown : undefined}
                  style={FILTER_STYLES.select}
                  value={filterValues[filterKey]}
                  onChange={handleDropdownChange(filterKey)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                >
                  {renderOptions(config.options)}
                </select>
              </div>
            );
          })}
        </div>
      ))}

      <div style={FILTER_STYLES.resetButtonContainer}>
        <button
          type="button"
          style={FILTER_STYLES.resetButton}
          onClick={handleReset}
          onFocus={handleFocus}
          onBlur={handleBlur}
        >
          Reset All
        </button>
      </div>
    </div>
  );
};

export default UserAssetFilter;
